// src/page/SummarySales.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

export default function SummarySales() {
    useAuthGuard();

    // === spot & ticket selects (same style as EventEdit.jsx) ===
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);
    const spotOptions = useMemo(
        () => spots
            .map(s => ({
                value: s.spotId ?? s.id ?? s.spot_id ?? '',
                label: s.spotName ?? s.name ?? s.spot_name ?? ''
            }))
            .filter(o => o.value && o.label),
        [spots]
    );
    const [ticketId, setTicketId] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const ticketOptions = useMemo(
        () => tickets
            .map(t => ({ value: t.packageId, label: t.packageName }))
            .filter(o => o.value && o.label),
        [tickets]
    );

    // fetch spots once
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                setSpots(list);
            } catch {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // fetch tickets when spot changes
    useEffect(() => {
        if (!spotId) { setTickets([]); setTicketId(''); return; }
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingTickets(true);
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/ticket`);
                url.searchParams.set('size', '200');
                url.searchParams.set('sort', 'title');
                url.searchParams.set('dir', 'asc');
                url.searchParams.set('spotid', spotId);
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                setTickets(Array.isArray(data.items) ? data.items : []);
            } catch {
                setTickets([]);
            } finally {
                setLoadingTickets(false);
            }
        })();
        return () => ac.abort();
    }, [spotId]);

    // === flatpickr for 照会期間 ===
    const rangeRef = useRef(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    const rangeText = fromDate && toDate ? `${fmt(fromDate)} ~ ${fmt(toDate)}` : '';

    useEffect(() => {
        if (!window.flatpickr) return;
        const ja = window.flatpickr.l10ns?.ja || {};
        const el = document.getElementById('summary_period_range');
        if (!el) return;

        const fp = window.flatpickr(el, {
            mode: 'range',
            dateFormat: 'Y/m/d',
            locale: { ...ja, rangeSeparator: ' ~ ' },
            onChange: (dates) => {
                if (dates?.length === 2) {
                    setFromDate(dates[0]);
                    setToDate(dates[1]);
                } else if (!dates?.length) {
                    setFromDate('');
                    setToDate('');
                }
            }
        });
        rangeRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    // === list (API) ===
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    // paging
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);
    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));
    
    const pad2 = (n) => String(n).padStart(2, '0');
    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d)) return '';
        return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    };
    const fmtInt = (v) => (v == null ? '' : String(v));
    const fmtJPY = (v) => (typeof v === 'number' ? v.toLocaleString('ja-JP') : (v ?? ''));

    // --- 一覧取得 ---
    const [search, setSearch] = useState({ spotId: '', packageId: '', from: '', to: '' });
    const toYMD = (d) => {
        if (!d) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
    };
    // --- 追加: sort state & toggle ---
    const [sort, setSort] = useState({ key: 'request', dir: 'desc' });
    const toggleSort = (key) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'asc' }
        );
        setPage(1);
    };
    //
    const handleSubmit = (e) => {
        e.preventDefault();
        setSearch({
            spotId,
            packageId: ticketId,
            from: fromDate ? toYMD(fromDate) : '',
            to: toDate ? toYMD(toDate) : '',
        });
        setPage(1);
    };
    const handleClear = () => {
        setSpotId('');
        setTickets([]);
        setTicketId('');
        setFromDate('');
        setToDate('');
        setSearch({ spotId: '', packageId: '', from: '', to: '' });
        setPage(1);
        // flatpickr もクリア
        try { rangeRef.current?.clear(); } catch { }
    };

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setListLoading(true);
                setListError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/sales`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                // filters
                if (search.spotId) url.searchParams.set('spotId', search.spotId);
                if (search.packageId) url.searchParams.set('packageId', search.packageId);
                if (search.from) url.searchParams.set('from', search.from);
                if (search.to) url.searchParams.set('to', search.to);
                // sort
                if (sort.key) url.searchParams.set('sort', sort.key);
                if (sort.dir) url.searchParams.set('dir', sort.dir);

                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();

                setRows(Array.isArray(j.items) ? j.items : []);
                setTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== pageSize) setPageSize(Number(j.size));
            } catch (e) {
                if (e && e.name === 'AbortError') return;
                console.error(e);
                setRows([]);
                setListError('読み込みに失敗しました');
            } finally {
                setListLoading(false);
            }
        })();
        return () => ac.abort();
    }, [page, pageSize, search, sort]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // 期間を ISO に（開始 00:00:00～終了 23:59:59）
    const toDayStartISO = (d) => {
        if (!d) return '';
        const dd = new Date(d); dd.setHours(0, 0, 0, 0); return dd.toISOString();
    };
    const toDayEndISO = (d) => {
        if (!d) return '';
        const dd = new Date(d); dd.setHours(23, 59, 59, 999); return dd.toISOString();
    };
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';
            const url = new URL(`${API_BASE_URL}/v1/summary/sales/export`);
            if (spotId) url.searchParams.set('spotid', spotId);
            if (ticketId) url.searchParams.set('packageid', ticketId);
            if (fromDate) url.searchParams.set('from', toDayStartISO(fromDate));
            if (toDate) url.searchParams.set('to', toDayEndISO(toDate));
            if (sort?.key) url.searchParams.set('sort', sort.key);
            if (sort?.dir) url.searchParams.set('dir', sort.dir);

            const res = await fetch(url.toString(), {
                headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);

            const pad = (n) => String(n).padStart(2, '0');
            const d = new Date();
            const fname = `購入実績_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.csv`;

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.error(e);
            alert('CSVのダウンロードに失敗しました');
        }
    };

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                    <li className="breadcrumb-item active">購入実績</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">購入実績</h2>
                    </div>
                </div>

                <div id="summary">
                    <form className="position-relative w-100" onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <Select
                                        options={spotOptions}
                                        value={spotOptions.find(o => o.value === spotId) || null}
                                        onChange={(opt) => setSpotId(opt?.value || '')}
                                        isDisabled={loadingSpots}
                                        isClearable
                                        placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                                <div className="search-box" style={{ minWidth: 280 }}>
                                    <Select
                                        options={ticketOptions}
                                        value={ticketOptions.find(o => o.value === ticketId) || null}
                                        onChange={(opt) => setTicketId(opt?.value || '')}
                                        isDisabled={!spotId || loadingTickets}
                                        isClearable
                                        placeholder={!spotId ? '' : (loadingTickets ? '読み込み中…' : '乗車券選択')}
                                        classNamePrefix="react-select"
                                        className="w-100"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                        filterOption={(opt, input) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
                            <div className="search-box">
                                <label className="form-label mb-1 d-block">照会期間</label>
                                <div className="position-relative">
                                    <input
                                        id="summary_period_range"
                                        className="form-control search-input search pe-5"
                                        type="text"
                                        name=""
                                        placeholder="YYYY/MM/DD ~ YYYY/MM/DD"
                                        value={rangeText}
                                        readOnly
                                    />
                                    <span
                                        className="fas fa-calendar-alt search-box-icon position-absolute top-50 translate-middle-y"
                                        style={{ left: 12, pointerEvents: 'none' }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="form-label mb-1 d-block">払い戻し</label>
                                <div className="d-flex flex-wrap gap-2">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" disabled />
                                        <label className="form-check-label" htmlFor="status-active">払い戻し可能</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" disabled />
                                        <label className="form-check-label" htmlFor="status-withdrawn">払い戻し済み</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                            <button type="submit" className="btn btn-primary">検索</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                            <div className="ms-xxl-auto d-flex gap-3">
                                <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                    <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                    <div className="table-responsive scrollbar mx-n1 px-1">
                        <table className="table fs-9 mb-0">

                            <thead>
                                <tr>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('spot')}>
                                        スポット名<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('package')}>
                                        乗車券名<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap">大人</th>
                                    <th className="align-middle ps-3 text-nowrap">小人</th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('request')}>
                                        購入日時<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('user')}>
                                        会員番号<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap" role="button">決済金額</th>
                                    <th className="align-middle ps-3 text-nowrap" role="button">取引ID</th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('error')}>
                                        エラーコード<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('status')}>
                                        購入ステータス<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap">払い戻し手数料</th>
                                    <th className="align-middle ps-3 text-nowrap">取り消し手数料</th>
                                    <th className="align-middle ps-3 text-nowrap" role="button"
                                        onClick={() => toggleSort('canceled')}>
                                        払い戻し時刻<span className="fas ms-1 text-muted fa-sort" />
                                    </th>
                                    <th className="align-middle ps-3 text-nowrap">キャンセルユーザー</th>
                                    <th className="align-middle ps-3"></th>
                                </tr>
                            </thead>

                            <tbody>
                                {listError && (
                                    <tr>
                                        <td className="ps-3 text-danger" colSpan={15}>{listError}</td>
                                    </tr>
                                )}
                                {!listError && listLoading && (
                                    <tr>
                                        <td className="ps-3 text-body-tertiary" colSpan={15}>読み込み中…</td>
                                    </tr>
                                )}
                                {!listError && !listLoading && rows.length === 0 && (
                                    <tr>
                                        <td className="ps-3 text-body-tertiary" colSpan={15}>データがありません</td>
                                    </tr>
                                )}
                                {!listError && !listLoading && rows.map((r, i) => (
                                    <tr key={i}>
                                        <td className="ps-3">{r.spotName || ''}</td>
                                        <td className="ps-3">{r.packageName || ''}</td>
                                        <td className="ps-3">{fmtInt(r.countAdult)}</td>
                                        <td className="ps-3">{fmtInt(r.countChild)}</td>
                                        <td className="ps-3">{fmtDateTime(r.requestDate)}</td>
                                        <td className="ps-3">{r.userNumber || ''}</td>
                                        <td className="ps-3">{fmtJPY(r.amount)}</td>
                                        <td className="ps-3">{r.tradeId || ''}</td>
                                        <td className="ps-3">{r.errorCode || ''}</td>
                                        <td className="ps-3">{r.paymentStatus || ''}</td>
                                        <td className="ps-3">{fmtJPY(r.refundFee)}</td>
                                        <td className="ps-3">{fmtJPY(r.cancellationFee)}</td>
                                        <td className="ps-3">{fmtDateTime(r.canceledAt)}</td>
                                        <td className="ps-3">{r.canceledBy || ''}</td>
                                        <td className="ps-3">
                                            <button className="btn btn-sm btn-outline-default" disabled>
                                                払い戻し
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                        <div className="col-auto d-flex align-items-center gap-3">
                            <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                            </p>
                        </div>

                        <div className="col-auto d-flex align-items-center">
                            <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                <span className="fas fa-chevron-left" />
                            </button>

                            <ul className="mb-0 pagination">
                                {pages.map(p => (
                                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                        <button type="button" className="page-link" onClick={() => setPage(p)}>
                                            {p}
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                <span className="fas fa-chevron-right" />
                            </button>
                        </div>
                    </div>

                </div>
            </div>

        </Layout >
    );
}
